/**
 * Closed-enum mirror of @puffin-core/marketing-contact-routes' 10 outcome
 * codes. The backend factory emits one of these in the response payload;
 * the frontend hook maps it through `errors.{outcome}` i18n keys for the
 * user-visible message.
 *
 * Keep aligned with `SUBMISSION_OUTCOMES` in marketing-contact-routes
 * (Phase 27 + Phase 27-hindsight E1–E15). The Phase Q cross-app contract
 * test asserts both arrays match.
 *
 * @module @puffin-core/marketing-shell/contact/contactOutcomes
 */

export const CONTACT_OUTCOMES = Object.freeze([
  'success',
  'honeypot',
  'validation_fail',
  'turnstile_fail',
  'rate_limited',
  'rate_limited_email',
  'origin_blocked',
  'persist_fail',
  'webhook_fail',
  'email_fail',
]);

/**
 * Outcomes that should render as a success state vs. an error state.
 * `email_fail` and `webhook_fail` are partial-success: the submission
 * went through but a downstream side effect failed. The user sees a
 * success state with a softer message; the consumer's analytics handler
 * sees the actual outcome so they can alert on the side-effect failure.
 */
export const SUCCESS_OUTCOMES = Object.freeze(['success', 'email_fail', 'webhook_fail']);

export function isContactOutcome(value) {
  return typeof value === 'string' && CONTACT_OUTCOMES.includes(value);
}

export function isSuccessOutcome(value) {
  return typeof value === 'string' && SUCCESS_OUTCOMES.includes(value);
}

/**
 * The outcomes a server answers when it refuses a submission as rate-limited: the generic per-address
 * limiter and the per-EMAIL limiter (whose translated message names the address). ONE predicate for the
 * hook (which locks the client throttle for the server's `retryAfter`) and the form (which renders that
 * refusal ONCE — the `<RateLimitNotice>` carrying this outcome's sentence, never a second banner).
 */
export const RATE_LIMITED_OUTCOMES = Object.freeze(['rate_limited', 'rate_limited_email']);

export function isRateLimitedOutcome(value) {
  return typeof value === 'string' && RATE_LIMITED_OUTCOMES.includes(value);
}
